import type { DirectoryOrigin, MapCoordinates } from "@vimos/api-client";
import { isValidLatitude, isValidLongitude } from "@vimos/api-client";

/**
 * Logica pura del modulo Directorio. Sin React ni Supabase: aqui viven las
 * reglas de visibilidad (espejo de la vista `stores_directory_public`), los
 * filtros del listado, las estadisticas y la validacion del formulario de
 * negocios manuales. Todo es testeable con datos de prueba.
 */

export type EditorialStatus = "visible" | "review" | "hidden";

/** Campos del listado. La ficha completa usa `StoreDirectoryRow` de api-client. */
export interface DirectoryStore {
  id: string;
  saas_store_id: string | null;
  slug: string;
  name: string;
  city: string;
  category: string;
  /** Id de la categoria controlada del catalogo (null = heredada/pendiente). */
  category_id?: string | null;
  /** Palabras clave de busqueda (max. 3). */
  keywords?: string[];
  description: string | null;
  store_url: string;
  logo_url: string | null;
  origin: DirectoryOrigin;
  is_visible_in_directory: boolean;
  is_force_hidden: boolean;
  is_featured: boolean;
  sort_order: number;
  editorial_status: EditorialStatus;
  synced_at: string | null;
  updated_at: string | null;
}

export type EditorialLabel = "Aprobada" | "En revision" | "Oculta editorial";
export type WebStatusLabel = "Visible en la web" | "No visible en la web";

/**
 * Estado publico real de un negocio. Espejo exacto de la vista publica:
 *   visible = is_visible_in_directory AND NOT is_force_hidden
 *             AND editorial_status = 'visible'
 * La aprobacion editorial NO publica; son dos estados distintos.
 */
export interface StoreState {
  isWebVisible: boolean;
  publicationLabel: "Publicado" | "Sin publicar";
  editorialLabel: EditorialLabel;
  webLabel: WebStatusLabel;
  /** Motivo legible por el que el negocio no (o si) aparece en /tiendas. */
  webDetail: string;
}

export function describeStoreState(store: {
  origin: DirectoryOrigin;
  is_visible_in_directory: boolean;
  is_force_hidden: boolean;
  editorial_status: EditorialStatus;
}): StoreState {
  const isWebVisible =
    store.is_visible_in_directory && !store.is_force_hidden && store.editorial_status === "visible";

  const editorialLabel: EditorialLabel =
    store.editorial_status === "visible"
      ? "Aprobada"
      : store.editorial_status === "review"
        ? "En revision"
        : "Oculta editorial";

  let webDetail: string;
  if (isWebVisible) {
    webDetail = "Aparece en /tiendas y en el sitemap.";
  } else if (store.is_force_hidden) {
    webDetail = "Bloqueado por el administrador (ocultar forzado).";
  } else if (store.editorial_status === "review") {
    webDetail = "La revision editorial aun no esta aprobada.";
  } else if (store.editorial_status === "hidden") {
    webDetail = "Oculto por revision editorial.";
  } else if (store.origin === "saas") {
    webDetail = "El SaaS informa que el dueno no lo tiene publicado en el directorio.";
  } else {
    webDetail = "Sin publicar desde el panel del directorio.";
  }

  return {
    isWebVisible,
    publicationLabel: store.is_visible_in_directory ? "Publicado" : "Sin publicar",
    editorialLabel,
    webLabel: isWebVisible ? "Visible en la web" : "No visible en la web",
    webDetail,
  };
}

/** Indicadores del encabezado. Se calculan sobre la lista ya cargada. */
export interface DirectoryStats {
  total: number;
  published: number;
  pendingReview: number;
  blocked: number;
  saas: number;
  manual: number;
}

export function computeDirectoryStats(stores: DirectoryStore[]): DirectoryStats {
  const stats: DirectoryStats = {
    total: stores.length,
    published: 0,
    pendingReview: 0,
    blocked: 0,
    saas: 0,
    manual: 0,
  };
  for (const store of stores) {
    if (describeStoreState(store).isWebVisible) stats.published += 1;
    if (store.editorial_status === "review") stats.pendingReview += 1;
    if (store.is_force_hidden) stats.blocked += 1;
    if (store.origin === "saas") stats.saas += 1;
    else stats.manual += 1;
  }
  return stats;
}

export type OriginFilter = "all" | DirectoryOrigin;
export type VisibilityFilter = "all" | "visible" | "unpublished" | "blocked";
export type EditorialFilter = "all" | EditorialStatus;
export type StoreSort = "featured" | "name_asc" | "name_desc" | "recent";

export interface DirectoryFilters {
  search: string;
  origin: OriginFilter;
  visibility: VisibilityFilter;
  category: string;
  editorial: EditorialFilter;
  sort: StoreSort;
}

export const defaultDirectoryFilters: DirectoryFilters = {
  search: "",
  origin: "all",
  visibility: "all",
  category: "",
  editorial: "all",
  sort: "featured",
};

export function hasActiveFilters(filters: DirectoryFilters): boolean {
  return (
    filters.search.trim() !== "" ||
    filters.origin !== "all" ||
    filters.visibility !== "all" ||
    filters.category !== "" ||
    filters.editorial !== "all" ||
    filters.sort !== "featured"
  );
}

/**
 * Coincide un negocio con el filtro de categoria: por etiqueta historica (texto)
 * o por id controlado. Un filtro de categoria general incluye sus subcategorias
 * (los ids hijos usan el prefijo `padre.`).
 */
function matchesCategoryFilter(store: DirectoryStore, filter: string): boolean {
  if (store.category === filter) return true;
  const id = store.category_id ?? null;
  if (id) {
    if (id === filter) return true;
    if (id.startsWith(`${filter}.`)) return true;
  }
  return false;
}

export function filterDirectoryStores(stores: DirectoryStore[], filters: DirectoryFilters): DirectoryStore[] {
  const needle = filters.search.trim().toLowerCase();
  const filtered = stores.filter((store) => {
    if (needle) {
      const haystack =
        `${store.name} ${store.city} ${store.category} ${(store.keywords ?? []).join(" ")}`.toLowerCase();
      if (!haystack.includes(needle)) return false;
    }
    if (filters.origin !== "all" && store.origin !== filters.origin) return false;
    if (filters.category !== "" && !matchesCategoryFilter(store, filters.category)) return false;
    if (filters.editorial !== "all" && store.editorial_status !== filters.editorial) return false;

    if (filters.visibility !== "all") {
      const state = describeStoreState(store);
      if (filters.visibility === "visible" && !state.isWebVisible) return false;
      if (filters.visibility === "unpublished" && store.is_visible_in_directory) return false;
      if (filters.visibility === "blocked" && !store.is_force_hidden) return false;
    }
    return true;
  });

  return sortDirectoryStores(filtered, filters.sort);
}

export function sortDirectoryStores(stores: DirectoryStore[], sort: StoreSort): DirectoryStore[] {
  const sorted = [...stores];
  switch (sort) {
    case "name_asc":
      sorted.sort((a, b) => a.name.localeCompare(b.name, "es"));
      break;
    case "name_desc":
      sorted.sort((a, b) => b.name.localeCompare(a.name, "es"));
      break;
    case "recent":
      sorted.sort((a, b) => (b.synced_at ?? "").localeCompare(a.synced_at ?? ""));
      break;
    case "featured":
    default:
      sorted.sort((a, b) => {
        if (a.is_featured !== b.is_featured) return a.is_featured ? -1 : 1;
        if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
        return a.name.localeCompare(b.name, "es");
      });
      break;
  }
  return sorted;
}

export function listCategories(stores: DirectoryStore[]): string[] {
  return Array.from(new Set(stores.map((store) => store.category).filter(Boolean))).sort((a, b) =>
    a.localeCompare(b, "es"),
  );
}

export const DIRECTORY_PAGE_SIZE = 20;

export interface PaginatedResult<T> {
  items: T[];
  page: number;
  totalPages: number;
  total: number;
}

export function paginateStores(
  stores: DirectoryStore[],
  page: number,
  pageSize: number = DIRECTORY_PAGE_SIZE,
): PaginatedResult<DirectoryStore> {
  const total = stores.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return { items: stores.slice(start, start + pageSize), page: safePage, totalPages, total };
}

/** Patch que envia el panel a la RPC `directory_admin_update`. */
export interface DirectoryAdminPatch {
  name?: string;
  city?: string;
  category?: string;
  category_id?: string | null;
  keywords?: string[];
  description?: string | null;
  department?: string | null;
  province?: string | null;
  district?: string | null;
  address?: string | null;
  phone?: string | null;
  whatsapp?: string | null;
  website_url?: string | null;
  store_url?: string | null;
  google_maps_url?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  logo_url?: string | null;
  cover_image_url?: string | null;
  gallery?: string[];
  social_links?: Record<string, string>;
  is_visible_in_directory?: boolean;
  is_force_hidden?: boolean;
  is_featured?: boolean;
  sort_order?: number;
  editorial_status?: EditorialStatus;
}

/** Formulario de edicion de negocios manuales. */
export interface StoreEditForm {
  name: string;
  category: string;
  city: string;
  department: string;
  province: string;
  district: string;
  address: string;
  phone: string;
  whatsapp: string;
  website_url: string;
  description: string;
  /** Coordenadas confirmadas desde el mapa (null = sin ubicacion precisa). */
  location: MapCoordinates | null;
}

export function storeToEditForm(store: {
  name: string;
  category: string;
  city: string;
  department: string | null;
  province: string | null;
  district: string | null;
  address: string | null;
  phone: string | null;
  whatsapp: string | null;
  website_url: string | null;
  description: string | null;
  latitude?: number | null;
  longitude?: number | null;
}): StoreEditForm {
  const latitude = store.latitude;
  const longitude = store.longitude;
  const location =
    typeof latitude === "number" &&
    typeof longitude === "number" &&
    isValidLatitude(latitude) &&
    isValidLongitude(longitude)
      ? { latitude, longitude }
      : null;

  return {
    name: store.name,
    category: store.category,
    city: store.city,
    department: store.department ?? "",
    province: store.province ?? "",
    district: store.district ?? "",
    address: store.address ?? "",
    phone: store.phone ?? "",
    whatsapp: store.whatsapp ?? "",
    website_url: store.website_url ?? "",
    description: store.description ?? "",
    location,
  };
}

function normalizeHttpUrl(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  try {
    const url = new URL(/^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`);
    if (!(url.protocol === "http:" || url.protocol === "https:") || !url.hostname.includes(".")) return null;
    const normalized = url.toString();
    return url.pathname === "/" && !url.search && !url.hash ? normalized.slice(0, -1) : normalized;
  } catch {
    return null;
  }
}

/** Devuelve un mapa campo -> mensaje. Vacio = formulario valido. */
export function validateStoreEditForm(form: StoreEditForm): Record<string, string> {
  const errors: Record<string, string> = {};
  const name = form.name.trim();
  if (name.length < 2 || name.length > 200) {
    errors.name = "El nombre comercial es obligatorio (entre 2 y 200 caracteres).";
  }
  if (!form.category.trim()) {
    errors.category = "La categoria es obligatoria.";
  }
  if (!form.city.trim()) {
    errors.city = "Indica la ciudad o ubicacion principal (se usa para agrupar en /tiendas).";
  }
  if (form.description.trim().length > 5000) {
    errors.description = "La descripcion no debe superar 5000 caracteres.";
  }
  if (form.website_url.trim() && !normalizeHttpUrl(form.website_url)) {
    errors.website_url = "La pagina web debe ser una URL http(s) valida.";
  }

  // Las coordenadas provienen del selector de mapa, que ya las valida; solo se
  // comprueba el rango por defensa adicional.
  if (
    form.location &&
    (!isValidLatitude(form.location.latitude) || !isValidLongitude(form.location.longitude))
  ) {
    errors.location = "La ubicacion del mapa no es valida.";
  }
  return errors;
}

/**
 * Patch con los cambios respecto al original (solo campos de contenido, para
 * negocios manuales). El estado de publicacion se envia por separado.
 */
export function buildManualContentPatch(original: StoreEditForm, form: StoreEditForm): DirectoryAdminPatch {
  const patch: DirectoryAdminPatch = {};
  const normalize = (value: string) => (value.trim() === "" ? null : value.trim());

  if (form.name.trim() !== original.name.trim()) patch.name = form.name.trim();
  if (form.category.trim() !== original.category.trim()) patch.category = form.category.trim();
  if (form.city.trim() !== original.city.trim()) patch.city = form.city.trim();

  const nullableFields = ["department", "province", "district", "address", "phone", "whatsapp"] as const;
  for (const field of nullableFields) {
    if (normalize(form[field] ?? "") !== normalize(original[field] ?? "")) {
      patch[field] = normalize(form[field]);
    }
  }

  const nextWebsite = normalizeHttpUrl(form.website_url);
  if (nextWebsite !== (normalizeHttpUrl(original.website_url) ?? normalize(original.website_url))) {
    patch.website_url = nextWebsite;
    // `store_url` es el enlace "Visitar tienda" de la ficha publica. Se
    // mantiene alineado con la web del negocio; si se limpia, se conserva el
    // ultimo valor para no dejar la ficha sin enlace.
    if (nextWebsite) patch.store_url = nextWebsite;
  }

  if (form.description.trim() !== original.description.trim()) {
    patch.description = form.description.trim() === "" ? null : form.description.trim();
  }

  // Ubicacion confirmada desde el mapa. Se conserva intacto `google_maps_url`
  // heredado: el selector ya no lo edita y nunca se sobreescribe.
  const nextLocation = form.location;
  const sameLocation =
    (nextLocation?.latitude ?? null) === (original.location?.latitude ?? null) &&
    (nextLocation?.longitude ?? null) === (original.location?.longitude ?? null);
  if (!sameLocation) {
    patch.latitude = nextLocation?.latitude ?? null;
    patch.longitude = nextLocation?.longitude ?? null;
  }
  return patch;
}

/**
 * Cambios editoriales de categoria controlada y palabras clave. A diferencia
 * del contenido, estos campos los gobierna Vimos y aplican tanto a negocios
 * manuales como del SaaS (la sincronizacion nunca los sobrescribe).
 */
export function buildCategoryKeywordsPatch(
  original: { category_id: string | null; keywords: string[] },
  next: { category_id: string | null; keywords: string[] },
): DirectoryAdminPatch {
  const patch: DirectoryAdminPatch = {};
  const nextCategoryId = next.category_id ?? null;
  if (nextCategoryId !== (original.category_id ?? null)) {
    patch.category_id = nextCategoryId;
  }
  const sameKeywords =
    original.keywords.length === next.keywords.length &&
    original.keywords.every((keyword, index) => keyword === next.keywords[index]);
  if (!sameKeywords) {
    patch.keywords = next.keywords;
  }
  return patch;
}

/**
 * Enlace de gestion en el SaaS para una tienda sincronizada. Solo existe una
 * ruta valida si el panel admin del SaaS esta configurado; si no, se usa la
 * tienda publica del SaaS como ultimo recurso informativo.
 */
export function saasManagementUrl(
  store: { saas_store_id: string | null; store_url: string },
  adminBaseUrl: string | null | undefined,
): string | null {
  const base = adminBaseUrl?.trim().replace(/\/+$/, "");
  if (base && store.saas_store_id) return `${base}/stores/${encodeURIComponent(store.saas_store_id)}`;
  if (store.store_url) return store.store_url;
  return null;
}

/** Traduce los errores de la RPC/Supabase a mensajes accionables. */
export function mapDirectoryError(error: unknown): string {
  if (!error || typeof error !== "object") return "Ocurrio un error inesperado.";
  const message = "message" in error ? String(error.message) : "";
  const code = "code" in error ? String(error.code) : "";

  if (code === "42501" || message.includes("insufficient_privilege")) {
    return "No tienes permisos para modificar el directorio. Se requiere rol admin o editor.";
  }
  if (code === "P0002" || message.includes("store_not_found")) {
    return "El negocio ya no existe en el directorio.";
  }
  if (code === "VD004" || message.includes("manual_store_has_open_submissions")) {
    return "No se puede eliminar esta ficha mientras tenga solicitudes abiertas. Resuelve o cierra primero las solicitudes asociadas.";
  }
  if (code === "VD005" || message.includes("manual_store_only")) {
    return "Solo se pueden eliminar fichas manuales. Las fichas SaaS se administran desde su sistema de origen.";
  }
  if (code === "VD001" || message.includes("saas_publication_managed_by_saas")) {
    return (
      "La publicacion de una tienda SaaS la gobierna el SaaS: el dueno debe activar " +
      "«Mostrar en directorio» alli y el cambio llega por sincronizacion. " +
      "Aqui solo puedes aprobar la revision o bloquearla en la web."
    );
  }
  if (code === "VD002" || message.includes("saas_content_managed_by_saas")) {
    return (
      "Los datos de una tienda SaaS se editan en el SaaS; se sobreescribirian en la " +
      "proxima sincronizacion. Este panel solo administra sus controles editoriales."
    );
  }
  if (code === "VD003" || message.includes("editorial_approval_required")) {
    return "Para publicar, primero aprueba la revision editorial del negocio.";
  }
  if (message.includes("unsupported_field")) {
    return "Se intento modificar un campo no permitido para este tipo de negocio.";
  }
  if (message.includes("invalid_google_maps_url")) {
    return (
      "El enlace de Google Maps no es valido. Usa un enlace de google.com/maps, " +
      "maps.google.com o maps.app.goo.gl, o ingresa las coordenadas en los campos de latitud y longitud."
    );
  }
  if (message.includes("invalid_")) {
    return "Algun dato enviado no es valido. Revisa los campos e intentalo de nuevo.";
  }
  if (code === "42883" || message.includes("directory_admin_update") || message.includes("Could not find")) {
    return (
      "Migracion pendiente en Supabase: aplica la ultima migracion del directorio " +
      "(directory_admin_update) para habilitar estas acciones."
    );
  }
  if (message.includes("Failed to fetch") || message.includes("NetworkError")) {
    return "Sin conexion con el servidor. Revisa tu red e intentalo otra vez.";
  }
  return message || "No se pudo completar la operacion.";
}
